import { useSyncExternalStore } from "react";

/**
 * Show/hide toggles for the 3D Fuel layer's parts, shared via a tiny external
 * store across the control UI, the tree/fire custom layer, and the 3D
 * buildings. Stored in localStorage (per device) — a pure display setting.
 */
export interface Fuel3DOptions {
  canopy: boolean;
  stems: boolean;
  buildings: boolean;
  fire: boolean;
  /** Surface layer: litter + near-surface shrubs + fallen/dead logs. */
  surface: boolean;
  /** Grass tufts (grasslands + grassy forest understory). */
  grass: boolean;
}

export type Fuel3DOptionKey = keyof Fuel3DOptions;

const STORAGE_KEY = "fisci.fuel3d_options";
const DEFAULTS: Fuel3DOptions = {
  canopy: true,
  stems: true,
  buildings: true,
  fire: true,
  surface: true,
  grass: true,
};
const listeners = new Set<() => void>();

const read = (): Fuel3DOptions => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const p = JSON.parse(raw) as Partial<Fuel3DOptions>;
    return {
      canopy: p.canopy ?? true,
      stems: p.stems ?? true,
      buildings: p.buildings ?? true,
      fire: p.fire ?? true,
      surface: p.surface ?? true,
      grass: p.grass ?? true,
    };
  } catch {
    return DEFAULTS;
  }
};

let current: Fuel3DOptions = read();

export const setFuel3DOption = (key: Fuel3DOptionKey, value: boolean): void => {
  current = { ...current, [key]: value };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    /* storage unavailable (private browsing) — keep the in-memory value */
  }
  listeners.forEach((listener) => listener());
};

export const useFuel3DOptions = (): Fuel3DOptions =>
  useSyncExternalStore(
    (callback) => {
      listeners.add(callback);
      return () => listeners.delete(callback);
    },
    () => current
  );
